import { useEffect, useState } from 'react';
import Icons from '../../../../../assets/index';
import Button from '../../../../../components/Button/Button';
import CustomTextAreaInput from '../../InputFields/CustomTextAreaInput';
import Dropdown from '@/components/Dropdown/Dropdown';
import CustomTextInput from '../../InputFields/CustomTextInput';

type Fields = {
  id: number;
  field_type: string;
};

interface Option {
  id: number;
  name: string;
  fields: Fields[];
}

interface FormData {
  options: Option[];
  helpText: string;
  defaultValue: string;
}

interface Props {
  onDataChange: (data: FormData) => void;
  shouldValidate: boolean;
  option?: Option[];
  helpText?: string;
  defaultValue?: string;
}

const actionOptions = [
  { label: 'Text Input', value: 'text_input' },
  { label: 'Text Area', value: 'text_area' },
  { label: 'File Upload', value: 'file' },
  { label: 'No Action', value: 'no_action' },
];

const ConditionDataType = ({
  onDataChange,
  shouldValidate,
  option,
  helpText,
  defaultValue,
}: Props) => {
  const [formData, setFormData] = useState<FormData>(() => {
    // If editing, map incoming options to ensure actions array exists
    if (option && Array.isArray(option) && option.length > 0) {
      return {
        options: option.map((opt, idx) => ({
          id: opt.id ?? idx + 1,
          name: opt.name ?? '',
          fields:
            Array.isArray(opt.fields) && opt.fields.length > 0
              ? opt.fields.map((a: any, aIdx) => ({
                  id: a.id ?? aIdx + 1,
                  field_type: a?.field_type ?? '',
                }))
              : [{ id: 1, field_type: '' }],
        })),
        helpText: helpText || '',
        defaultValue: defaultValue || '',
      };
    }
    // Default: two options, each with one empty action
    return {
      options: [
        { id: 1, name: 'Yes', fields: [{ id: 1, field_type: '' }] },
        { id: 2, name: 'No', fields: [{ id: 1, field_type: '' }] },
      ],
      helpText: helpText || '',
      defaultValue: defaultValue || '',
    };
  });

  const [nextOptionId, setNextOptionId] = useState(3);
  const [duplicateError, setDuplicateError] = useState(false);
  const [actionErrors, setActionErrors] = useState<{ [key: string]: boolean }>({});

  const hasDuplicateName = (option: Option, idx: number) =>
    formData.options.some(
      (opt, i) =>
        i !== idx &&
        opt.name.trim().toLowerCase() === option.name.trim().toLowerCase() &&
        option.name.trim() !== ''
    );

  useEffect(() => {
    // Check for duplicate names
    const optionNames = formData.options
      .map((option) => option.name.trim().toLowerCase())
      .filter((name) => name !== '');
    const hasDuplicates = new Set(optionNames).size !== optionNames.length;
    setDuplicateError(hasDuplicates);

    // Check for empty action types
    const errors: { [key: string]: boolean } = {};
    formData.options.forEach((opt, optIdx) => {
      opt.fields.forEach((field, fieldIdx) => {
        if (shouldValidate && !field.field_type?.trim()) {
          errors[`field-${optIdx}-${fieldIdx}`] = true;
        }
      });
    });
    setActionErrors(errors);
  }, [formData.options, shouldValidate]);

  const handleNameChange = (optionIdx: number, value: string) => {
    const updatedFormData = {
      ...formData,
      options: formData.options.map((option, i) =>
        i === optionIdx ? { ...option, name: value } : option
      ),
    };
    setFormData(updatedFormData);
    onDataChange(updatedFormData);
  };

  const handleActionTypeChange = (optionIdx: number, fieldIdx: number, value: string) => {
    const updatedFormData = {
      ...formData,
      options: formData.options.map((option, i) =>
        i === optionIdx
          ? {
              ...option,
              fields: option.fields.map((field, j) =>
                j === fieldIdx ? { ...field, field_type: value } : field
              ),
            }
          : option
      ),
    };
    setFormData(updatedFormData);
    onDataChange(updatedFormData);

    // Clear error for this action if value is provided
    if (value) {
      setActionErrors((prev) => ({ ...prev, [`field-${optionIdx}-${fieldIdx}`]: false }));
    }
  };

  const addOption = () => {
    // Validate existing actions before adding new option
    const errors: { [key: string]: boolean } = {};
    let hasError = false;

    formData.options.forEach((opt, optIdx) => {
      opt.fields.forEach((field, fieldIdx) => {
        if (!field.field_type?.trim()) {
          errors[`field-${optIdx}-${fieldIdx}`] = true;
          hasError = true;
        }
      });
    });

    if (hasError) {
      setActionErrors(errors);
      return;
    }

    const newOption: Option = {
      id: nextOptionId,
      name: '',
      fields: [{ id: 1, field_type: '' }],
    };

    const updatedFormData = {
      ...formData,
      options: [...formData.options, newOption],
    };
    setFormData(updatedFormData);
    onDataChange(updatedFormData);
    setNextOptionId((prev) => prev + 1);
  };

  const removeOption = (optionIdx: number) => {
    if (formData.options.length <= 2 || optionIdx < 2) return;

    const updatedFormData = {
      ...formData,
      options: formData.options.filter((_, i) => i !== optionIdx),
    };
    setFormData(updatedFormData);
    onDataChange(updatedFormData);
  };

  const addActionToOption = (optionIdx: number) => {
    const updatedFormData = {
      ...formData,
      options: formData.options.map((option, i) =>
        i === optionIdx
          ? {
              ...option,
              fields: [
                ...option.fields,
                {
                  id: option.fields.length + 1,
                  field_type: '',
                },
              ],
            }
          : option
      ),
    };
    setFormData(updatedFormData);
    onDataChange(updatedFormData);
  };

  const removeActionFromOption = (optionIdx: number, fieldIdx: number) => {
    const updatedFormData = {
      ...formData,
      options: formData.options.map((option, i) =>
        i === optionIdx && option.fields.length > 1
          ? {
              ...option,
              fields: option.fields.filter((_, j) => j !== fieldIdx),
            }
          : option
      ),
    };
    setFormData(updatedFormData);
    onDataChange(updatedFormData);
  };

  const getActionLabel = (optionIdx: number, fieldIdx: number) => {
    const actionCount = formData.options[optionIdx].fields.length;
    if (actionCount === 1) {
      return 'Action';
    }
    return `Action ${fieldIdx + 1}`;
  };

  return (
    <div className="flex flex-col ml-4">
      {/* Help Text */}
      <div className="mb-6">
        <CustomTextAreaInput
          label="Help Text"
          name="helpText"
          value={formData.helpText}
          onChange={(e) => {
            const updatedFormData = { ...formData, helpText: e.target.value };
            setFormData(updatedFormData);
            onDataChange(updatedFormData);
          }}
          placeholder="Enter the description. i.e - This section provides key points of contact for communication and support."
          description="This will help in guiding the person filling this field."
          rows={10}
          textAreaClassname="w-[298px]"
        />
      </div>

      {/* Default Value */}
      <div className="w-[360px] mb-6">
        <CustomTextInput
          label="Default Value"
          name="defaultValue"
          value={formData.defaultValue}
          onChange={(e) => {
            const updatedFormData = { ...formData, defaultValue: e.target.value };
            setFormData(updatedFormData);
            onDataChange(updatedFormData);
          }}
          className="text-ds-gray-500 font-semibold text-xs"
        />
      </div>

      {/* Options */}
      <div className="">
        {formData.options.map((option, optionIdx) => (
          <div key={option.id} className="mb-6">
            {/* Option Name */}
            <div className="mb-4">
              <label className="text-xs text-ds-gray-500 font-semibold">
                Option {optionIdx + 1} <span className="text-ds-red-400">*</span>
              </label>

              <div className="flex items-center mt-2">
                <input
                  type="text"
                  value={option.name}
                  onChange={(e) => handleNameChange(optionIdx, e.target.value)}
                  className={`h-10 w-full border rounded-md px-3 text-ds-gray-500 outline-none placeholder:text-ds-gray-300 text-xs ${
                    hasDuplicateName(option, optionIdx) ? 'border-[#DC3545]' : 'border-[#E0E0E0]'
                  }`}
                  placeholder="Enter option"
                />

                {optionIdx >= 2 && (
                  <button
                    onClick={() => removeOption(optionIdx)}
                    type="button"
                    className="ml-2"
                    title="Remove Option"
                  >
                    <Icons.trashIcon />
                  </button>
                )}
              </div>

              {hasDuplicateName(option, optionIdx) && (
                <div className="text-[#DC3545] text-sm mt-1">
                  Duplicate options are not allowed.
                </div>
              )}
              {shouldValidate && !option.name.trim() && (
                <div className="text-[#DC3545] text-sm mt-1">Option name is required.</div>
              )}
            </div>

            {/* Actions Section */}
            <div className="mb-3">
              {/* Render all actions for this option */}
              {option.fields.map((field, fieldIdx) => {
                return (
                  <div key={`${option.id}-${field.id}`} className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs text-ds-gray-500 font-semibold">
                        {getActionLabel(optionIdx, fieldIdx)}{' '}
                        <span className="text-ds-red-400">*</span>
                      </label>
                      {option.fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeActionFromOption(optionIdx, fieldIdx)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove Action"
                        >
                          <Icons.trashIcon />
                        </button>
                      )}
                    </div>

                    <div className="mb-3">
                      <p className="text-xs text-ds-gray-300 mb-2">
                        Select the action for option {optionIdx + 1} selection.
                      </p>
                      <Dropdown
                        options={actionOptions}
                        selectedValue={field.field_type}
                        onChange={(val) => handleActionTypeChange(optionIdx, fieldIdx, val)}
                        error={actionErrors[`field-${optionIdx}-${fieldIdx}`]}
                        errorMessage="Input type cannot be empty. Please select an option from the dropdown list."
                      />
                    </div>

                    {/* Conditional fields based on action type */}
                    {field.field_type === 'file' && (
                      <div className="mt-3">
                        <label className="text-xs text-ds-gray-500 font-semibold">
                          File Upload Settings
                        </label>
                        <div className="text-xs text-ds-gray-300 mt-1">
                          File upload action configured.
                        </div>
                      </div>
                    )}

                    {field.field_type === 'no_action' && (
                      <div className="mt-3">
                        <div className="text-xs text-ds-gray-300">
                          No additional action will be performed.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add Action Button */}
              <button
                type="button"
                className="text-blue-400 text-xs hover:text-blue-600 flex items-center gap-1 no-underline"
                onClick={() => addActionToOption(optionIdx)}
              >
                <Icons.plusIcon className="w-3 h-3" />
                Add Action
              </button>
            </div>

            {/* Separator line between options */}
            {optionIdx < formData.options.length - 1 && (
              <div className="w-full h-[1px] bg-ds-gray-100 my-4"></div>
            )}
          </div>
        ))}
      </div>

      {/* Global duplicate error */}
      {duplicateError && (
        <div className="text-[#DC3545] text-sm mb-4">Duplicate options are not allowed.</div>
      )}

      {/* Add Option Button */}
      <div className="mt-3">
        <Button onClick={addOption} type="button" startIcon={<Icons.plusIcon />}>
          Add Option
        </Button>
      </div>
    </div>
  );
};

export default ConditionDataType;
